import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SERVICE_SESSION_EVENT, endKioskSession, getServiceToken, hasServiceCapability, isFreePrintEnabled, setFreePrintEnabled } from "../../services/kioskServiceSession";
import ServiceDialog from "./ServiceDialog";
import ServicePaperPanel from "./ServicePaperPanel";
import ServiceCommandsPanel from "./ServiceCommandsPanel";
import "./service.css";

const TABS = ["paper", "print", "diagnostics", "recovery"];

export default function ServicePanel() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tab, setTab] = useState("paper");
    const [session, setSession] = useState(() => Boolean(getServiceToken()));
    const [freePrint, setFreePrint] = useState(isFreePrintEnabled);
    const [busy, setBusy] = useState("");
    const [exitOpen, setExitOpen] = useState(false);

    useEffect(() => {
        const sync = () => { setSession(Boolean(getServiceToken())); setFreePrint(isFreePrintEnabled()); };
        window.addEventListener(SERVICE_SESSION_EVENT, sync);
        return () => window.removeEventListener(SERVICE_SESSION_EVENT, sync);
    }, []);
    const exit = () => { endKioskSession(); navigate("/", { replace: true }); };
    if (!session) return (
        <div className="service-panel"><section className="service-card service-empty">
            <h1 className="service-title">{t("service.ended_title")}</h1>
            <p className="service-subtitle">{t("service.ended_text")}</p>
            <button data-analytics="servicepanel.service.home.1" className="service-button primary" onClick={exit}>{t("service.home")}</button>
        </section></div>
    );
    return (
        <div className="service-panel">
            <header className="service-header">
                <div><h1 className="service-title">{t("service.title")}</h1><p className="service-subtitle">{t("service.subtitle")}</p></div>
                <button data-analytics="servicepanel.service.to_documents.2" className="service-button" disabled={Boolean(busy)} onClick={() => navigate("/printing/show_list/user")}>{t("service.to_documents")}</button>
            </header>
            <nav className="service-nav" aria-label={t("service.sections_label")}>
                {TABS.map((id) => <button data-analytics="servicepanel.setTab.3" key={id} aria-pressed={tab === id} onClick={() => setTab(id)}>{t(`service.tabs.${id}`)}</button>)}
            </nav>
            <div className="service-body">
                <section aria-label={t("service.tabs.paper")} className="service-view" hidden={tab !== "paper"}><ServicePaperPanel busy={busy} setBusy={setBusy} /></section>
                <section aria-label={t("service.tabs.print")} className="service-view" hidden={tab !== "print"}>
                    <div className="service-scroll"><div className="service-card">
                        <div className="service-toolbar">
                            <div><h2>{t("service.print.title")}</h2><p className="service-subtitle">{t("service.print.subtitle")}</p></div>
                            <span className="service-badge">{t(freePrint ? "service.print.badge_free" : "service.print.badge_paid")}</span>
                        </div>
                        <div className="service-toolbar">
                            <div><h3 id="free-print-label">{t("service.print.free_title")}</h3><p className="service-subtitle">{t(freePrint ? "service.print.free_on" : "service.print.free_off")}</p></div>
                            <button data-analytics="servicepanel.setFreePrintEnabled.4" className="service-switch" role="switch" aria-labelledby="free-print-label" aria-checked={freePrint}
                                disabled={Boolean(busy) || !hasServiceCapability("free_print")}
                                onClick={() => setFreePrintEnabled(!freePrint)}><span /></button>
                        </div>
                        {!hasServiceCapability("free_print") && <p className="service-message">{t("service.print.free_unavailable")}</p>}
                        <ol className="service-subtitle space-y-[1vh] my-[2vh]">
                            <li>{t("service.print.step1")}</li><li>{t("service.print.step2")}</li><li>{t("service.print.step3")}</li>
                        </ol>
                        <button data-analytics="servicepanel.service.print.choose_document.5" className="service-button primary" disabled={Boolean(busy)} onClick={() => navigate("/printing/show_list/user")}>{t("service.print.choose_document")}</button>
                        <p className="service-subtitle">{t("service.print.until_end")}</p>
                    </div></div>
                </section>
                <section aria-label={t("service.commands_label")} className="service-view" hidden={tab !== "diagnostics" && tab !== "recovery"}>
                    <ServiceCommandsPanel category={tab === "recovery" ? "recovery" : "diagnostics"} busy={busy} setBusy={setBusy} />
                </section>
            </div>
            <footer className="service-footer">
                <p role="status" title={busy || t("service.footer_hint")}>{busy || t("service.footer_hint")}</p>
                <button data-analytics="servicepanel.service.end_session.6" className="service-button" onClick={() => setExitOpen(true)}>{t("service.end_session")}</button>
            </footer>
            {exitOpen && <ServiceDialog title={t("service.exit.title")} onClose={() => setExitOpen(false)}>
                <p className="service-subtitle">{t("service.exit.text")}</p>
                {busy && <p className="service-message mt-[2vh]">{t("service.exit.busy")}</p>}
                <div className="service-actions"><button data-analytics="servicepanel.service.exit.stay.7" className="service-button" onClick={() => setExitOpen(false)}>{t("service.exit.stay")}</button><button data-analytics="servicepanel.service.end_session.8" className="service-button primary" onClick={exit}>{t("service.end_session")}</button></div>
            </ServiceDialog>}
        </div>
    );
}
